// Public domain models for the RealityCheq corpus.
// Service-level types that describe corpus entities — consumed by both the backend
// pipeline and the platform. No route-specific request/response shapes here.

import { SCORECARD_VOCAB, type ScorecardKey } from "@shared/scorecard.ts";
import type { TaxonomyUrn } from "@shared/types/taxonomy.ts";

// ── Scorecard ─────────────────────────────────────────────────────────────────

/** Scorecard labels per dimension, keyed by ScorecardKey. Derived from SCORECARD_VOCAB. */
export type ExtractedScorecard = Record<ScorecardKey, string[]>;

export function emptyScorecard(): ExtractedScorecard {
  return Object.fromEntries(
    Object.keys(SCORECARD_VOCAB).map((k) => [k, []])
  ) as unknown as ExtractedScorecard;
}

// ── LLM extraction ────────────────────────────────────────────────────────────

/**
 * Structural descriptor for a metadata field as it appears in the annotation UI.
 * Kept separate from the backend-only hint/parse so the platform can import just this.
 */
export interface MetadataField {
  key: string;
  /** Value is string[] in the DB — edited as comma-separated in the UI. */
  isArray?: boolean;
  /** Render as textarea rather than single-line input. */
  multiline?: boolean;
}

/** Describes how a single metadata field is extracted from the LLM response. */
export interface LlmExtract extends MetadataField {
  hint: string;
  /** Custom parser for the raw LLM value. If omitted, defaults to string coercion or null. */
  parse?: (v: unknown) => unknown;
}

/**
 * Ordered list of all LLM-extracted metadata fields shown in the annotation UI.
 * This is the single source of truth for field order, array type, and multiline hint.
 *
 * The backend (corpus/config.ts) imports this and augments each entry with
 * LLM prompt hints and optional parse functions. The platform imports it directly
 * from @shared/corpus to drive the MetadataPanel without duplicating the field list.
 */
export const METADATA_LLM_FIELDS: MetadataField[] = [
  { key: "title", multiline: true },
  { key: "authors", isArray: true, multiline: true },
  { key: "publication_date" },
  { key: "doi", multiline: true },
  { key: "country" },
  { key: "keywords", isArray: true, multiline: true },
  { key: "exposure", multiline: true },
  { key: "outcome", multiline: true },
  { key: "cohort", multiline: true },
  { key: "p_value", multiline: true },
  { key: "loss_to_follow_up", multiline: true },
  { key: "confounding_variables", multiline: true },
  { key: "bradford_hill", multiline: true },
  { key: "generalisability", multiline: true },
  { key: "surrogate_endpoints", multiline: true },
];

// ── Queue / source workflow ───────────────────────────────────────────────────

/**
 * Mirrors the source_status PostgreSQL enum.
 * pending   — Tier-1 confidence gate not yet cleared (escalated or never reviewed).
 * ready     — gate cleared; scorecard is final and eligible to embed, not yet embedded.
 * ingested  — embedded and searchable.
 * revoked — was ingested, then pulled from search (embeddings deleted); row and stored
 *             section text are kept, reversibly.
 */
export type SourceStatus = "pending" | "ready" | "ingested" | "revoked";

/** Review state of a single scorecard dimension set by the annotator. */
export type DimensionReviewState = "ai_approved" | "ai_overridden" | "human_filled" | "empty";

export interface AnnotationDimension {
  labels: string[];
  state: DimensionReviewState;
}

export interface DimensionPreAnnotation {
  labels: string[];
  quote: string | null;
  /** Extraction confidence 0.0–1.0. Null when produced by the legacy TS pipeline (no score). */
  confidence: number | null;
  /** The tags of the Tangrowth steps that shaped these labels, comma-joined in the order they
   *  acted, e.g. `llm.study_type.261009,nlp_rule.study_type.261009`; each is
   *  `<method>.<key>.<version>`, the version a date written YYMMDD. Absent on rows written
   *  before versions were stored; null when nothing was found. */
  algorithm_version?: string | null;
  /** The model's argument for these labels, kept for fields whose evidence is an argument rather
   *  than a verbatim quote (study_type); null for the others. Absent on older rows. */
  justification?: string | null;
}

export type AnnotationMap = Record<string, AnnotationDimension>;

// ── Tier-cleared status (display-only, TAN-33/34) ─────────────────────────────

export type TierClearedState = "cleared" | "blocked";

/** Per-tier cleared/blocked status derived from confidence_scores against
 *  AUTO_VALIDATE_THRESHOLD. Display-only — does not reflect ingestion gating, which only
 *  Tier 1 participates in (see evaluateIngestDecision). */
export interface TierClearedStatus {
  tier1: TierClearedState;
  tier2: TierClearedState;
  tier3: TierClearedState;
}

/** Scorecard with LLM-suggested labels, evidence quotes, and resolved character ranges.
 *  Uses ScorecardKey for strict key enumeration. */
export type ExtractedScorecardWithEvidence = Record<ScorecardKey, DimensionPreAnnotation>;

// ── Scorecard payload — versioned envelope ─────────────────────────────────────
// ai_scorecard in annotation_workflow stores one of these envelopes.
// Discriminate on `kind` to know which shape you're holding; `v` (semver) versions
// the schema within that kind so additions inside a kind don't require a new kind value.

/** One model's draft for a single field — labels plus the evidence span that justified them. */
export interface CandidateDraft {
  labels: string[];
  evidence: string | null;
}

/** A field the deterministic pipeline settled from an authoritative source at the fast-path
 *  threshold, so it was never sent to the model panel. Carried alongside the multi-candidate
 *  drafts so the review UI can pre-validate it (shown pre-approved, reviewer confirms) instead
 *  of dropping it — see the MCA fast-path/pre-validation design. `confidence` is the pipeline's
 *  calibrated confidence; `method` is its ExtractionMethod (e.g. "api"). */
export interface PrevalidatedField {
  labels: string[];
  evidence: string | null;
  confidence: number;
  method: string;
}

/** One model's full Pass-2 output — one CandidateDraft per scorecard field it covered. */
export type FieldCandidateDrafts = Partial<Record<ScorecardKey, CandidateDraft>>;

/** Per-field consensus signal stored alongside a multi-candidate scorecard. */
export interface MultiScorecardConsensusField {
  /** Mean pairwise Jaccard across active candidates for this field; 0.0–1.0. */
  jaccard: number;
}

/** Document-level consensus summary stored inside a MultiScorecardPayload. */
export interface MultiScorecardConsensus {
  /** Mean field-level Jaccard across fields with ≥2 active candidates; 0.0–1.0. */
  score: number;
  fields: Partial<Record<ScorecardKey, MultiScorecardConsensusField>>;
}

/** Mono-candidate scorecard envelope — wraps ExtractedScorecardWithEvidence with kind/v metadata. */
export interface MonoScorecardPayload {
  kind: "mono";
  v: string;
  scorecard: ExtractedScorecardWithEvidence;
}

/** Multi-candidate scorecard envelope — N independent model drafts per field with consensus data.
 *  Used by ensemble annotation workflows (e.g. MCA Pass-2). */
export interface MultiScorecardPayload {
  kind: "multi";
  v: string;
  /** Model name → their full-document draft (one CandidateDraft per field covered). */
  candidates: Record<string, FieldCandidateDrafts>;
  consensus: MultiScorecardConsensus;
  /** Fields the pipeline settled and pre-validated (fast-path); not drafted by the panel, so
   *  absent from `candidates`/`consensus`. Optional — payloads written before v1.1.0 omit it;
   *  read it as an empty map. */
  prevalidated?: Partial<Record<ScorecardKey, PrevalidatedField>>;
}

/** Versioned scorecard payload stored in annotation_workflow.ai_scorecard.
 *  Discriminate on `kind`; `v` (semver) versions the schema within a kind. */
export type ScorecardPayload = MonoScorecardPayload | MultiScorecardPayload;

/** The discriminant of ScorecardPayload — `"mono"` or `"multi"`. */
export type ScorecardKind = ScorecardPayload["kind"];

/** The deterministic pipeline's voter name in a multi payload's `candidates` map — the
 *  current-practice baseline whose labels are promoted when collapsing a multi payload to a mono
 *  scorecard (matches `_PIPELINE_VOTER` in tangrowth's `tools/mca/consensus.py`). */
const PIPELINE_VOTER = "pipeline";

/** Accessor that collocates all kind-branching for ScorecardPayload — construct once per call
 *  site instead of scattering kind checks across functions. The static factory covers
 *  construction so the version string lives in one place alongside the type definition. */
export class ScorecardPayloadView {
  /** Wrap an extracted mono scorecard in the versioned mono envelope. */
  static mono(scorecard: ExtractedScorecardWithEvidence): MonoScorecardPayload {
    return { kind: "mono", v: "1.1.0", scorecard };
  }

  constructor(readonly payload: ScorecardPayload | null) {}

  get kind(): "mono" | "multi" | null {
    return this.payload?.kind ?? null;
  }

  /** The extracted mono scorecard, or null for multi-candidate or absent payloads. */
  get monoScorecard(): ExtractedScorecardWithEvidence | null {
    return this.payload?.kind === "mono" ? this.payload.scorecard : null;
  }

  /** The single merged mono scorecard to score, gate on, and display — regardless of payload kind.
   *  Mono returns its own scorecard as-is. Multi collapses candidates + prevalidated + consensus
   *  into one scorecard: a prevalidated (fast-path) field keeps its authoritative labels +
   *  confidence; every other field takes the pipeline voter's labels (falling back to any active
   *  voter if the pipeline errored on it) with the payload's per-field agreement as confidence —
   *  labels come from the pipeline, how sure we are comes from cross-voter agreement, so a
   *  contested field is still labelled but carries a low confidence. A null/absent payload returns
   *  an empty scorecard. This is the projection the ingest gate, the annotate merge, and Kodakku's
   *  per-field surfaces all read, so mono and multi never diverge. */
  get effectiveScorecard(): ExtractedScorecardWithEvidence {
    if (this.payload?.kind === "mono") return this.payload.scorecard;
    if (this.payload?.kind !== "multi") return {} as ExtractedScorecardWithEvidence;

    const confidence = this.confidenceScores ?? ({} as Record<ScorecardKey, number>);
    const candidates = this.candidates ?? {};
    const prevalidated = this.prevalidated;
    const pipeline = candidates[PIPELINE_VOTER] ?? {};

    const keys = new Set<ScorecardKey>();
    for (const key of Object.keys(prevalidated)) keys.add(key as ScorecardKey);
    for (const draft of Object.values(candidates)) {
      for (const key of Object.keys(draft)) keys.add(key as ScorecardKey);
    }

    const merged = {} as ExtractedScorecardWithEvidence;
    for (const key of keys) {
      const pv = prevalidated[key];
      if (pv) {
        merged[key] = {
          labels: pv.labels,
          quote: pv.evidence,
          confidence: pv.confidence,
        };
        continue;
      }
      const draft = pipeline[key] ?? Object.values(candidates).find((c) => key in c)?.[key];
      if (!draft) continue;
      merged[key] = {
        labels: draft.labels,
        quote: draft.evidence,
        confidence: confidence[key] ?? 0,
      };
    }
    return merged;
  }

  /** Document-level agreement score (0.0–1.0); null for mono or absent payloads. */
  get consensusScore(): number | null {
    return this.payload?.kind === "multi" ? this.payload.consensus.score : null;
  }

  /** study_type labels for effectiveDimensions — mono uses its own scorecard; multi prefers a
   *  prevalidated (fast-path) study_type, else the first candidate's labels as a hint (no merged
   *  mono scorecard exists yet). */
  get studyTypeLabels(): string[] {
    if (this.payload?.kind === "mono") {
      return this.payload.scorecard.study_type?.labels ?? [];
    }
    if (this.payload?.kind === "multi") {
      const prevalidated = this.payload.prevalidated?.study_type?.labels;
      if (prevalidated && prevalidated.length > 0) return prevalidated;
      return Object.values(this.payload.candidates)[0]?.study_type?.labels ?? [];
    }
    return [];
  }

  /** The per-model draft map, or null for mono or absent payloads. */
  get candidates(): Record<string, FieldCandidateDrafts> | null {
    return this.payload?.kind === "multi" ? this.payload.candidates : null;
  }

  /** Pipeline-settled, pre-validated fields (fast-path); empty for mono, absent, or pre-1.1.0
   *  multi payloads. Keyed by ScorecardKey. */
  get prevalidated(): Partial<Record<ScorecardKey, PrevalidatedField>> {
    return this.payload?.kind === "multi" ? (this.payload.prevalidated ?? {}) : {};
  }

  /** Per-field extraction confidence derived from a multi-candidate payload, for the ingest gate
   *  (evaluateIngestDecision) and the annotation backlog — null for mono or absent payloads, whose
   *  confidence lives in the stored `confidence_scores` column instead. A field's confidence is:
   *    - a prevalidated (fast-path) field's own calibrated confidence — an authoritative source
   *      clears on its own, even with no model votes (e.g. API study_type with Kimi absent); else
   *    - the panel's per-field agreement (mean pairwise Jaccard) when ≥2 voters actively drafted it; else
   *    - 0 — a single active voter is not agreement (its stored Jaccard is a vacuous 1.0), so it must
   *      not auto-clear the gate; the field escalates to a human.
   *  study_type is the only field this gates on (Tier 1, design-documents/035), so the ≥2-voter
   *  guard is what stops a lone model draft of the one verdict-changing field settling unreviewed. */
  get confidenceScores(): Record<ScorecardKey, number> | null {
    if (this.payload?.kind !== "multi") return null;
    const { candidates, consensus, prevalidated } = this.payload;
    const result = {} as Record<ScorecardKey, number>;

    for (const [key, field] of Object.entries(prevalidated ?? {})) {
      if (field) result[key as ScorecardKey] = field.confidence;
    }

    for (const [key, field] of Object.entries(consensus.fields)) {
      if (!field || (key as ScorecardKey) in result) continue; // prevalidated wins
      const activeVoters = Object.values(candidates).filter(
        (draft) => (key as ScorecardKey) in draft
      ).length;
      result[key as ScorecardKey] = activeVoters >= 2 ? field.jaccard : 0;
    }

    return result;
  }
}

/** Subset returned by GET /corpus/documents (list view).
 *  id is sources.id — the stable handle for all /corpus/documents/:id routes. */
export interface QueueItem {
  id: string;
  url: string;
  title: string | null;
  status: SourceStatus;
  submitted_at: string;
  annotated_at: string | null;
  ingested_at: string | null;
  /** current_score / potential_score at last extraction/annotation, in [0, 1]. Null for rows
   *  predating Tangrowth rollout. */
  rank_stability: number | null;
  /** Per-field extraction confidence. Null for rows predating Tangrowth rollout. */
  confidence_scores: Record<ScorecardKey, number> | null;
  /** Derived from confidence_scores; null when confidence_scores is null. */
  tier_cleared: TierClearedStatus | null;
}

// ── QueueRow discriminated union ──────────────────────────────────────────────
// Full source + annotation_workflow row returned by GET /corpus/documents/:id.
// Discriminate on `status` to know which fields are guaranteed to be present.

interface QueueRowBase extends QueueItem {
  metadata: Record<string, unknown> | null;
  /** Versioned scorecard payload — discriminate on `kind` before reading field-level data. */
  ai_scorecard: ScorecardPayload | null;
  /** Taxonomy field URNs derived by Tangrowth, the `taxonomy_nodes` field URNs. Empty until TAX-07 lands. */
  fields: TaxonomyUrn[];
  doi: string | null;
  authors: string | null;
  source_type: string | null;
  published_date: string | null;
  /** Fetched from Supabase Storage on demand; always populated in the GET /documents/:id response. */
  markdown_text?: string;
}

/** Source submitted but not yet annotated by a human, or annotated but still blocked on one or
 *  more Tier-1 fields (TAN-28: a save re-runs the ingest decision and stays pending if it
 *  doesn't clear — annotation is non-null in that case, holding whatever was resolved so far). */
export interface PendingQueueRow extends QueueRowBase {
  status: "pending";
  annotation: AnnotationMap | null;
  scorecard: null;
}

/** Tier-1 gate cleared: scorecard is final and eligible to embed, not yet embedded. */
export interface ReadyQueueRow extends QueueRowBase {
  status: "ready";
  annotation: AnnotationMap;
  scorecard: Record<string, string[]>;
}

/** Embedded and searchable: ready for claim verification search. */
export interface IngestedQueueRow extends QueueRowBase {
  status: "ingested";
  annotation: AnnotationMap;
  scorecard: Record<string, string[]>;
}

/** A source pulled from search after ingestion; its annotation and scorecard are kept as-is. */
export interface RevokedQueueRow extends QueueRowBase {
  status: "revoked";
  annotation: AnnotationMap;
  scorecard: Record<string, string[]>;
}

export type QueueRow = PendingQueueRow | ReadyQueueRow | IngestedQueueRow | RevokedQueueRow;
